/**
 * A picture being cropped, as Photoshop shows it: the whole picture darkened outside the part
 * kept (`.board-crop-shade`), thirds over the part kept (`#board-crop`), L-shaped corners and
 * side bars (`[data-crop-handle]`) the same size on screen whatever the zoom, and under it the
 * bar with the size, Reset, Cancel and Apply (`#board-crop-bar`).
 */
import { For, Show, type JSX } from "solid-js";
import { useApp } from "../../../app/context";
import { pictureOf } from "../../../shared/assets";
import { HANDLES, type Cropping, type Rect } from "../../../state/board";
import { CROP_BAR_GAP_PX, CROP_HANDLE_PX } from "./constants";

/** A rectangle's place in the layer (board units). */
const rectCss = (r: Rect): JSX.CSSProperties => ({ left: `${r.x}px`, top: `${r.y}px`, width: `${r.w}px`, height: `${r.h}px` });

/** The picture `cropping` names, with its handles and its bar. */
export function CropOverlay(props: { cropping: Cropping }): JSX.Element {
  const { board, graph, assets } = useApp();
  const c = () => props.cropping;
  /** One screen pixel in board units. */
  const px = () => 1 / board.camera().k;
  const src = () => {
    const path = pictureOf(graph.state.graph.nodes.find((n) => n.id === c().id)?.attachments);
    return path ? assets.url(path) : null;
  };
  return (
    <Show when={src()}>
      {(s) => (
        <>
          {/* The whole picture, darkened outside the part kept, as Photoshop shows it. */}
          <div class="board-crop-whole" style={rectCss(c().whole)}>
            <img src={s()} alt="" draggable={false} />
            <div class="board-crop-shade" style={rectCss({ x: c().box.x - c().whole.x, y: c().box.y - c().whole.y, w: c().box.w, h: c().box.h })} />
          </div>
          <div class="board-crop-box" data-crop-box="yes" id="board-crop" style={{ ...rectCss(c().box), outline: `${px()}px solid #fff`, "--crop-px": `${px()}px` }}>
            <div class="board-crop-thirds" aria-hidden="true">
              <span class="v" style={{ left: "33.333%" }} />
              <span class="v" style={{ left: "66.667%" }} />
              <span class="h" style={{ top: "33.333%" }} />
              <span class="h" style={{ top: "66.667%" }} />
            </div>
            <For each={HANDLES}>
              {(h) => {
                /** Corners are L-shaped, sides short bars, all the same size on screen. */
                const corner = h.length === 2;
                const long = () => (corner ? CROP_HANDLE_PX.corner : CROP_HANDLE_PX.side) * px();
                const thick = () => CROP_HANDLE_PX.thick * px();
                const hit = () => CROP_HANDLE_PX.hit * px();
                const across = h === "n" || h === "s";
                return (
                  <span
                    class={`board-crop-handle h-${h}`}
                    classList={{ corner }}
                    data-crop-handle={h}
                    style={{
                      left: `${h.includes("w") ? 0 : h.includes("e") ? 100 : 50}%`,
                      top: `${h.includes("n") ? 0 : h.includes("s") ? 100 : 50}%`,
                      width: `${corner ? long() : across ? long() : hit()}px`,
                      height: `${corner ? long() : across ? hit() : long()}px`,
                      margin: corner
                        ? `${h.includes("n") ? -thick() : thick() - long()}px 0 0 ${h.includes("w") ? -thick() : thick() - long()}px`
                        : `${-(across ? hit() : long()) / 2}px 0 0 ${-(across ? long() : hit()) / 2}px`,
                      "--crop-thick": `${thick()}px`,
                    }}
                  />
                );
              }}
            </For>
          </div>
          {/* Apply or cancel, under the part kept, at the same size on screen whatever the zoom. */}
          <div
            id="board-crop-bar"
            class="board-crop-bar"
            style={{ left: `${c().box.x + c().box.w / 2}px`, top: `${c().box.y + c().box.h}px`, transform: `scale(${px()}) translate(-50%, ${CROP_BAR_GAP_PX}px)` }}
            onPointerDown={(e) => e.stopPropagation()}
            onDblClick={(e) => e.stopPropagation()}
          >
            <span class="board-crop-size">
              {Math.round(c().box.w)} × {Math.round(c().box.h)}
            </span>
            <button type="button" id="board-crop-reset" title="Keep the whole picture" onClick={() => board.resetCrop()}>
              Reset
            </button>
            <button type="button" id="board-crop-cancel" title="Cancel (Esc)" onClick={() => void board.endCrop(false)}>
              ✕ Cancel
            </button>
            <button type="button" id="board-crop-apply" class="primary" title="Apply (Enter)" onClick={() => void board.endCrop(true)}>
              ✓ Apply
            </button>
          </div>
        </>
      )}
    </Show>
  );
}
